export const CODE_SEARCH_PORTAL_ORIGIN = 'https://code-search-portal.atlassian.com';

/** The portal's results view; `explore` is its single-file viewer. */
const SEARCH_VIEW = 'search';

/** DevAI reports Bitbucket as `bbc`; the portal expects `bitbucket`. */
const SCM_ALIASES: Readonly<Record<string, string>> = { bbc: 'bitbucket' };

export type BuildCodeSearchPortalUrlOptions = {
	searchQuery: string;
	/** Full file path; only its directory is used. */
	path?: string | null;
	scm?: string | null;
	repositoryName?: string | null;
	/** Bitbucket workspace or GitHub organisation. */
	namespace?: string | null;
};

/**
 * The directory for the link's `path:` qualifier, or `''` to leave it out.
 * Left out for a root-level file, and for a directory with a space in it, because the Code
 * search portal reads a qualifier only up to the first space.
 *
 * - `src/auth/session/store.ts` -> `src/auth/session`
 * - `README.md` -> `''`
 * - `src/my docs/notes.md` -> `''`
 */
export const getPathScope = (path?: string | null): string => {
	const directory = path && path.includes('/') ? path.slice(0, path.lastIndexOf('/')) : '';
	return /\s/.test(directory) ? '' : directory;
};

/** Builds a Code Search Portal link scoped to a result's directory, repository, workspace and code host. */
export const buildCodeSearchPortalUrl = ({
	searchQuery,
	path,
	scm,
	repositoryName,
	// namespace,
}: BuildCodeSearchPortalUrlOptions): string => {
	// Same order the Code search portal writes into its own search box.
	// There is an existing bug in the Code search portal's `namespace:` filter, so the qualifier
	// is not emitted. Once it is fixed, uncomment it here and the `namespace` parameter above.
	const qualifiers: Array<[name: string, value?: string | null]> = [
		['scm', scm ? (SCM_ALIASES[scm] ?? scm) : scm],
		// ['namespace', namespace],
		['repo', repositoryName],
		['path', getPathScope(path)],
	];

	const query = [
		searchQuery.trim(),
		...qualifiers.filter(([, value]) => !!value).map(([name, value]) => `${name}:${value}`),
	]
		.filter(Boolean)
		.join(' ');

	const params = `view=${SEARCH_VIEW}${query ? `&q=${encodeURIComponent(query)}` : ''}`;
	return `${CODE_SEARCH_PORTAL_ORIGIN}/code?${params}`;
};
